import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Form } from "antd";
import { FacultySettingsFormModel } from "./models/FacultySettingsFormModel";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../../Notifications/NotificationsUtils";
import { useQuery } from "react-query";
import { FacultySettingsAddDTO, FacultySettingsDTO } from "../../../Api";
import { FacultySettingsFormController } from "./FacultySettingsForm.types";
import dayjs from "../../../utils/dayjs-setup";
import {
	addFacultySettings,
	getFacultySettings,
	updateFacultySettings,
} from "../../../Requests/faculty-settings-requests";
import { useIsDean, getUserId } from "../../../utils/utilFunctions";
import {
	getFacultiesStudyPrograms,
	getSpecificProfessor,
} from "../../../utils/reactQueriesConstants";
import { getStudyProgramsForFaculties } from "../../../Requests/student-study-requests";
import { getProfessorById } from "../../../Requests/academic-user-requests";

export const useFacultySettingsFormController =
	(): FacultySettingsFormController => {
		const { t } = useTranslation();
		const [form] = Form.useForm<FacultySettingsFormModel>();
		const [settingsId, setSettingsId] = useState("");
		const [editEnabled, setEditEnabled] = useState(false);
		const isDean = useIsDean();
		const currentUserId = getUserId();

		const { data: currentUser } = useQuery(
			[getSpecificProfessor, currentUserId, isDean],
			() => {
				if (isDean) {
					return getProfessorById(currentUserId);
				}
				return undefined;
			},
			{
				onError: (err) => {
					console.warn("Failed to fetch professor profile:", err);
				},
				refetchOnWindowFocus: false,
				retry: false,
				enabled: isDean,
			}
		);

		const { data: settings } = useQuery(
			[getFacultySettings, isDean],
			() => getFacultySettings(),
			{
				onError: (err) => {
					openGetErrorNotification(err);
				},
				onSuccess: (data: FacultySettingsDTO) => {
					if (data) {
						form.setFieldsValue({
							practiceInternshipPeriods: data.practiceInternshipPeriods
								?.sort((a, b) => a.practiceYear!.localeCompare(b.practiceYear!))
								.map((period) => {
									return {
										studyProgramId: period.studyProgramId,
										practiceYear: period.practiceYear,
										practiceEvaluationStart: dayjs(
											period.practiceEvaluationStart
										),
										practiceEvaluationEnd: dayjs(period.practiceEvaluationEnd),
										practiceNumberOfHours: period.practiceNumberOfHours,
										practiceECTS: period.practiceECTS,
									};
								}),
							decisionNumber: data.decisionNumber,
							internshipLimits: data.internshipLimits?.sort((a, b) =>
								a.internshipType!.localeCompare(b.internshipType!)
							),
							facultyPhone: data.facultyContactInformation?.phone,
							facultyEmail: data.facultyContactInformation?.email,
							facultyContactPersonName:
								data.facultyContactInformation?.contactPersonName,
						});
						setSettingsId(data.id!);
					}
					return data;
				},
				refetchOnWindowFocus: false,
			}
		);

		const { data: studyPrograms } = useQuery(
			[getFacultiesStudyPrograms, currentUser?.faculty?.id, isDean],
			() => {
				if (isDean && currentUser?.faculty?.id) {
					return getStudyProgramsForFaculties([currentUser.faculty.id]);
				}
				return undefined;
			},
			{
				onError: (err) => {
					openGetErrorNotification(err);
				},
				refetchOnWindowFocus: false,
				enabled: isDean && !!currentUser?.faculty?.id,
				select: (data) => {
					return data?.filter((program) => program.studyCycle === "Bachelor");
				},
			}
		);

		const openGetErrorNotification = (_error: any) => {
			openNotification(
				t("facultySettings.error"),
				t("facultySettings.fetchDataError"),
				NOTIFICATION_TYPES.ERROR
			);
		};

		const openSuccessEditNotification = () => {
			openNotification(
				t("facultySettings.editSetting"),
				t("facultySettings.editSuccess"),
				NOTIFICATION_TYPES.SUCCESS,
				6
			);
		};

		const openSuccessAddNotification = () => {
			openNotification(
				t("facultySettings.addSetting"),
				t("facultySettings.addSuccess"),
				NOTIFICATION_TYPES.SUCCESS,
				6
			);
		};

		const openSaveErrorNotification = (error: any) => {
			openNotification(
				t("internships.error"),
				t("internships.saveDataError"),
				NOTIFICATION_TYPES.ERROR
			);
		};

		const onFinish = () => {
			const state = form.getFieldsValue();

			const body: FacultySettingsAddDTO = {
				...state,
				facultyContactInformation: {
					phone: state.facultyPhone,
					email: state.facultyEmail,
					contactPersonName: state.facultyContactPersonName,
				},
			};

			(settingsId ? updateFacultySettings(body) : addFacultySettings(body))
				.then(
					settingsId ? openSuccessEditNotification : openSuccessAddNotification
				)
				.catch(openSaveErrorNotification);
			setEditEnabled(false);
		};
		const handleCancel = () => {
			setEditEnabled(false);
		};

		const handleEdit = () => {
			setEditEnabled(true);
		};

		return {
			state: {
				form,
				settings,
				settingsId,
				editEnabled,
				studyPrograms,
			},
			actions: {
				onFinish,
				handleEdit,
				handleCancel,
			},
			computed: null,
		};
	};
